/**
 * A tiny widget kit drawn into a canvas, because in a headset there is no DOM to lay out.
 *
 * The panel is a texture on a quad; a controller ray hits it, the hit becomes a pixel, and the
 * pixel becomes a widget. Everything here is measured in that canvas's pixels.
 */

export const PANEL_W = 660;
export const ROW_H = 78;
export const PAD = 22;

export type Widget =
  | { kind: "section"; text: string }
  | { kind: "note"; label: string; value: string }
  | { kind: "slider"; id: string; label: string; text: string; value: number; min: number; max: number; step: number }
  | { kind: "choice"; id: string; label: string; value: string; options: { value: string; label: string }[] }
  | { kind: "button"; id: string; label: string; tone?: "warn" | "primary"; disabled?: boolean }
  /** Buttons side by side in one row: a small panel's worth of controls without a tall panel. */
  | { kind: "buttons"; buttons: Omit<Extract<Widget, { kind: "button" }>, "kind">[] };

export type Hit =
  | { widget: Extract<Widget, { kind: "button" }>; }
  | { widget: Extract<Widget, { kind: "choice" }>; option: string }
  | { widget: Extract<Widget, { kind: "slider" }>; fraction: number };

type Placed = { widget: Widget; y: number; height: number };

const INK = "#e8ecf1";
const DIM = "#8c98a6";
const LINE = "#242a31";
const ACCENT = "#5b9dff";
const WARN = "#f5a623";

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Shrink the type until the line fits, rather than letting it run off the panel. */
function fitted(ctx: CanvasRenderingContext2D, text: string, width: number, size: number, weight = "400"): void {
  let px = size;
  do {
    ctx.font = `${weight} ${px}px ui-sans-serif, system-ui, sans-serif`;
    px -= 2;
  } while (px > 14 && ctx.measureText(text).width > width);
}

export class Panel {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private placed: Placed[] = [];
  height = 100;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.width = PANEL_W;
    this.canvas.height = 1200;
    this.ctx = this.canvas.getContext("2d")!;
  }

  layout(widgets: Widget[]): number {
    let y = PAD;
    this.placed = widgets.map((widget) => {
      const height = widget.kind === "section" ? 54 : widget.kind === "note" ? 40 : ROW_H;
      const item = { widget, y, height };
      y += height;
      return item;
    });
    this.height = y + PAD;
    if (this.canvas.height < this.height) this.canvas.height = Math.ceil(this.height / 100) * 100;
    return this.height;
  }

  draw(cursor: { x: number; y: number } | null, dragging: string | null): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = "rgba(12, 15, 19, 0.94)";
    rounded(ctx, 0, 0, PANEL_W, this.height, 26);
    ctx.fill();
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 2;
    ctx.stroke();

    for (const { widget, y, height } of this.placed) {
      const hot = cursor !== null && cursor.y >= y && cursor.y < y + height;
      switch (widget.kind) {
        case "section": {
          fitted(ctx, widget.text, PANEL_W - 2 * PAD, 26, "600");
          ctx.fillStyle = DIM;
          ctx.textBaseline = "middle";
          ctx.fillText(widget.text.toUpperCase(), PAD, y + height / 2);
          break;
        }
        case "note": {
          ctx.textBaseline = "middle";
          fitted(ctx, widget.label, 240, 24);
          ctx.fillStyle = DIM;
          ctx.fillText(widget.label, PAD, y + height / 2);
          const room = PANEL_W - 2 * PAD - (widget.label ? 250 : 0);   // no label: the whole row is the value
          fitted(ctx, widget.value, room, 24);
          ctx.fillStyle = INK;
          ctx.textAlign = "right";
          ctx.fillText(widget.value, PANEL_W - PAD, y + height / 2);
          ctx.textAlign = "left";
          break;
        }
        case "slider": {
          ctx.textBaseline = "middle";
          fitted(ctx, widget.label, 380, 26);
          ctx.fillStyle = INK;
          ctx.fillText(widget.label, PAD, y + 24);
          fitted(ctx, widget.text, 200, 26);
          ctx.fillStyle = dragging === widget.id ? ACCENT : DIM;
          ctx.textAlign = "right";
          ctx.fillText(widget.text, PANEL_W - PAD, y + 24);
          ctx.textAlign = "left";
          const trackY = y + 54;
          const w = PANEL_W - 2 * PAD;
          ctx.fillStyle = "#222932";
          rounded(ctx, PAD, trackY - 7, w, 14, 7);
          ctx.fill();
          const t = (widget.value - widget.min) / (widget.max - widget.min || 1);
          ctx.fillStyle = ACCENT;
          rounded(ctx, PAD, trackY - 7, Math.max(14, w * t), 14, 7);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(PAD + w * t, trackY, dragging === widget.id ? 20 : 16, 0, Math.PI * 2);
          ctx.fillStyle = INK;
          ctx.fill();
          break;
        }
        case "choice": {
          ctx.textBaseline = "middle";
          fitted(ctx, widget.label, 300, 26);
          ctx.fillStyle = INK;
          ctx.fillText(widget.label, PAD, y + 22);
          const w = (PANEL_W - 2 * PAD) / widget.options.length;
          widget.options.forEach((option, i) => {
            const x = PAD + i * w;
            const on = option.value === widget.value;
            ctx.fillStyle = on ? ACCENT : "#1c2229";
            rounded(ctx, x + 3, y + 40, w - 6, 30, 8);
            ctx.fill();
            fitted(ctx, option.label, w - 16, 24, on ? "600" : "400");
            ctx.fillStyle = on ? "#06121f" : DIM;
            ctx.textAlign = "center";
            ctx.fillText(option.label, x + w / 2, y + 55);
            ctx.textAlign = "left";
          });
          break;
        }
        case "button": {
          drawButton(ctx, widget, PAD, y, PANEL_W - 2 * PAD, height, hot);
          break;
        }
        case "buttons": {
          const w = (PANEL_W - 2 * PAD) / widget.buttons.length;
          widget.buttons.forEach((b, i) => {
            const x = PAD + i * w;
            const over = hot && cursor !== null && cursor.x >= x && cursor.x < x + w;
            drawButton(ctx, b, x + (i ? 5 : 0), y, w - (widget.buttons.length > 1 ? 5 : 0), height, over);
          });
          break;
        }
      }
    }

    if (cursor) {
      ctx.beginPath();
      ctx.arc(cursor.x, cursor.y, 7, 0, Math.PI * 2);
      ctx.fillStyle = ACCENT;
      ctx.fill();
    }
  }

  /** What is under this pixel, if anything can be pressed there. */
  hit(x: number, y: number): Hit | null {
    for (const { widget, y: top, height } of this.placed) {
      if (y < top || y >= top + height) continue;
      if (widget.kind === "button") return widget.disabled ? null : { widget };
      if (widget.kind === "buttons") {
        const w = (PANEL_W - 2 * PAD) / widget.buttons.length;
        const b = widget.buttons[Math.max(0, Math.min(widget.buttons.length - 1, Math.floor((x - PAD) / w)))];
        return b.disabled ? null : { widget: { kind: "button", ...b } };
      }
      if (widget.kind === "slider") {
        const w = PANEL_W - 2 * PAD;
        return { widget, fraction: Math.max(0, Math.min(1, (x - PAD) / w)) };
      }
      if (widget.kind === "choice") {
        const w = (PANEL_W - 2 * PAD) / widget.options.length;
        const index = Math.max(0, Math.min(widget.options.length - 1, Math.floor((x - PAD) / w)));
        return { widget, option: widget.options[index].value };
      }
      return null;
    }
    return null;
  }

  /** A slider being dragged keeps control of the pointer even when it wanders off the track. */
  sliderById(id: string): Extract<Widget, { kind: "slider" }> | null {
    for (const { widget } of this.placed) {
      if (widget.kind === "slider" && widget.id === id) return widget;
    }
    return null;
  }
}

function drawButton(ctx: CanvasRenderingContext2D, b: { label: string; tone?: "warn" | "primary"; disabled?: boolean },
                    x: number, y: number, w: number, height: number, hot: boolean): void {
  const colour = b.tone === "warn" ? WARN : b.tone === "primary" ? ACCENT : INK;
  ctx.fillStyle = b.disabled ? "#161a20" : hot ? "#222b35" : "#1c2229";
  rounded(ctx, x, y + 8, w, height - 18, 10);
  ctx.fill();
  ctx.strokeStyle = hot && !b.disabled ? colour : LINE;
  ctx.lineWidth = 2;
  ctx.stroke();
  fitted(ctx, b.label, w - 30, 27, "500");
  ctx.fillStyle = b.disabled ? "#4a535d" : colour;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText(b.label, x + w / 2, y + height / 2);
  ctx.textAlign = "left";
}

export function valueFromFraction(slider: { min: number; max: number; step: number }, fraction: number): number {
  const raw = slider.min + fraction * (slider.max - slider.min);
  const stepped = Math.round(raw / slider.step) * slider.step;
  return Math.max(slider.min, Math.min(slider.max, Number(stepped.toFixed(4))));
}
